"use client";

import * as React from "react";
import { EditorContent, EditorContext, useEditor } from "@tiptap/react";

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { Mention } from "@tiptap/extension-mention";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import { Placeholder } from "@tiptap/extensions";
import { Typography } from "@tiptap/extension-typography";
import { Highlight } from "@tiptap/extension-highlight";
import { Superscript } from "@tiptap/extension-superscript";
import { Subscript } from "@tiptap/extension-subscript";
import { TextAlign } from "@tiptap/extension-text-align";
import { Mathematics } from "@tiptap/extension-mathematics";
import { UniqueID } from "@tiptap/extension-unique-id";
import { Emoji, gitHubEmojis } from "@tiptap/extension-emoji";

// --- Custom Extensions ---
import { HorizontalRule } from "@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";
import { UiState } from "@/components/tiptap-extension/ui-state-extension";

// --- Tiptap Node ---
import { ImageUploadNode } from "@/components/tiptap-node/image-upload-node/image-upload-node-extension";

// --- Node Styles ---
import "@/components/tiptap-node/blockquote-node/blockquote-node.scss";
import "@/components/tiptap-node/code-block-node/code-block-node.scss";
import "@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss";
import "@/components/tiptap-node/list-node/list-node.scss";
import "@/components/tiptap-node/image-node/image-node.scss";
import "@/components/tiptap-node/heading-node/heading-node.scss";
import "@/components/tiptap-node/paragraph-node/paragraph-node.scss";

// --- Tiptap UI Components ---
import { EmojiDropdownMenu } from "@/components/tiptap-ui/emoji-dropdown-menu";
import { MentionDropdownMenu } from "@/components/tiptap-ui/mention-dropdown-menu";
import { SlashDropdownMenu } from "@/components/tiptap-ui/slash-dropdown-menu";
import { DragContextMenu } from "@/components/tiptap-ui/drag-context-menu";

// --- Contexts (AI only, no collaboration) ---
import { AppProvider } from "@/contexts/app-context";
import { UserProvider } from "@/contexts/user-context";

// --- Hooks ---
import { useUiEditorState } from "@/hooks/use-ui-editor-state";

// --- Utilities ---
import { handleImageUpload, MAX_FILE_SIZE } from "@/lib/tiptap-utils";

// --- Styles ---
import "@/components/tiptap-templates/notion-like/notion-like-editor.scss";

// --- Header and Toolbar Components ---
import { NotionEditorHeader } from "@/components/tiptap-templates/notion-like/notion-like-editor-header";
import { NotionToolbarFloating } from "@/components/tiptap-templates/notion-like/notion-like-editor-toolbar-floating";
import { MobileToolbar } from "@/components/tiptap-templates/notion-like/notion-like-editor-mobile-toolbar";

export interface NotionLikeEditorProps {
  content?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  editable?: boolean;
}

/**
 * Loading spinner component
 */
export function LoadingSpinner({ text = "Loading..." }: { text?: string }) {
  return (
    <div className="spinner-container">
      <div className="spinner-content">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <div className="spinner-loading-text">{text}</div>
      </div>
    </div>
  );
}

/**
 * Main editor content area
 */
function EditorContentArea() {
  const editorState = useUiEditorState();

  return (
    <div className="notion-like-editor-content">
      <div className="notion-like-editor-scroll-container">
        <EditorContent
          className="notion-like-editor-editor-content"
          style={{ whiteSpace: "pre-wrap" }}
        />
        <NotionToolbarFloating />
        <MobileToolbar />
        <EmojiDropdownMenu />
        <MentionDropdownMenu />
        <SlashDropdownMenu />
        <DragContextMenu />
      </div>
    </div>
  );
}

/**
 * Simplified NotionEditor without collaboration features
 */
export function NotionLikeEditor({
  content = "",
  onChange,
  placeholder = "Start writing...",
  editable = true,
}: NotionLikeEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable History from StarterKit since we're not using collaboration
        history: {
          depth: 100,
          newGroupDelay: 1000,
        },
      }),
      Placeholder.configure({ placeholder }),
      Mathematics.configure({
        HTMLAttributes: {
          class: "math-node",
          "data-math-display": "block",
        },
      }),
      HorizontalRule,
      Mention.configure({
        suggestion: {
          items: ({ query }) => {
            // Simple mention items - you can customize this
            const items = [
              { id: "1", label: "User 1" },
              { id: "2", label: "User 2" },
              { id: "3", label: "User 3" },
            ];
            return items
              .filter((item) =>
                item.label.toLowerCase().includes(query.toLowerCase()),
              )
              .slice(0, 5);
          },
        },
      }),
      Emoji.configure({
        emojis: gitHubEmojis,
        enableEmoticons: true,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Subscript,
      Superscript,
      Color,
      TextStyle,
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Image,
      ImageUploadNode.configure({
        accept: "image/*",
        maxSize: MAX_FILE_SIZE,
        limit: 3,
        upload: handleImageUpload,
        onError: (error) => console.error("Upload failed:", error),
      }),
      UniqueID,
      Typography,
      UiState,
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl mx-auto focus:outline-none",
      },
    },
  });

  if (!editor) {
    return <LoadingSpinner />;
  }

  return (
    <div className="notion-like-editor-wrapper">
      <EditorContext.Provider value={{ editor }}>
        <NotionEditorHeader />
        <EditorContentArea />
      </EditorContext.Provider>
    </div>
  );
}

/**
 * Full NotionLike editor with providers (without collaboration)
 */
export function NotionLikeEditorWithProviders(props: NotionLikeEditorProps) {
  return (
    <UserProvider>
      <AppProvider>
        <NotionLikeEditor {...props} />
      </AppProvider>
    </UserProvider>
  );
}
